import { create } from 'zustand';
import { fileAPI } from '../utils/axios';
import { handleApiError } from '../utils/errorHandler';

const useFileStore = create((set, get) => ({
  // 상태
  files: [],
  pagination: {
    current_page: 1,
    total_pages: 0,
    total_files: 0,
    has_next: false,
    has_prev: false
  },
  loading: false,
  error: null,
  
  // 필터 및 정렬 옵션
  filters: {
    status: 'active',        // 'active', 'deleted', 'all'
    page: 1,
    limit: 20,
    sort_by: 'uploaded_at',  // 'uploaded_at', 'filename', 'file_size'
    sort_order: 'desc',      // 'asc', 'desc'
    search: ''
  },

  // 파일 목록 로드
  loadFiles: async (options = {}) => {
    const currentFilters = get().filters;
    const newFilters = { ...currentFilters, ...options };
    
    set({ loading: true, error: null, filters: newFilters });

    try {
      console.log('[FILE_STORE]', 'Loading files with filters:', newFilters);
      
      const result = await fileAPI.getFileList(newFilters);

      if (result.success) {
        set({
          files: result.data.files || [],
          pagination: result.data.pagination || {
            current_page: 1,
            total_pages: 0,
            total_files: 0,
            has_next: false,
            has_prev: false
          },
          loading: false,
          error: null
        });

        console.log('[FILE_STORE]', 'Files loaded successfully:', {
          count: result.data.files?.length || 0,
          total: result.data.pagination?.total_files || 0
        });

      } else {
        throw new Error(result.message);
      }

    } catch (error) {
      console.error('[FILE_STORE]', 'Load files error:', error);
      const errorInfo = handleApiError(error);
      
      set({
        files: [],
        pagination: {
          current_page: 1,
          total_pages: 0,
          total_files: 0,
          has_next: false,
          has_prev: false
        },
        loading: false,
        error: errorInfo.message
      });
    }
  },

  // 페이지 변경
  changePage: async (page) => {
    const { loadFiles } = get();
    await loadFiles({ page });
  },

  // 정렬 변경
  changeSort: async (sort_by, sort_order = 'desc') => {
    const { loadFiles } = get();
    await loadFiles({ sort_by, sort_order, page: 1 }); // 정렬 변경시 첫 페이지로
  },

  // 필터 변경
  changeFilter: async (status) => {
    const { loadFiles } = get();
    await loadFiles({ status, page: 1 }); // 필터 변경시 첫 페이지로
  },

  // 검색
  searchFiles: async (searchTerm) => {
    const { loadFiles } = get();
    await loadFiles({ search: searchTerm, page: 1 }); // 검색시 첫 페이지로
  },

  // 새 파일 추가 (업로드 완료 후)
  addFile: (newFile) => {
    const { files } = get();
    
    // 변환된 파일 데이터 형태로 맞추기
    const standardizedFile = {
      file_id: newFile.id,
      filename: newFile.fileName || newFile.filename,
      description: newFile.fileDescription || newFile.description || '',
      file_size: newFile.file_size,
      file_type: newFile.file_type,
      status: 'active',
      uploaded_at: newFile.uploaded_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    set({
      files: [standardizedFile, ...files], // 새 파일을 맨 앞에 추가
      pagination: {
        ...get().pagination,
        total_files: get().pagination.total_files + 1
      }
    });

    console.log('[FILE_STORE]', 'New file added to store:', standardizedFile);
  },

  // 파일 업데이트 (재업로드, 이름 변경 등)
  updateFile: (fileId, updates) => {
    const { files } = get();
    
    const updatedFiles = files.map(file => 
      file.file_id === fileId 
        ? { ...file, ...updates, updated_at: new Date().toISOString() }
        : file
    );

    set({ files: updatedFiles });
    console.log('[FILE_STORE]', 'File updated:', fileId, updates);
  },

  // 파일 삭제
  removeFile: (fileId) => {
    const { files } = get();
    
    const updatedFiles = files.filter(file => file.file_id !== fileId);
    
    set({
      files: updatedFiles,
      pagination: {
        ...get().pagination,
        total_files: Math.max(0, get().pagination.total_files - 1)
      }
    });

    console.log('[FILE_STORE]', 'File removed from store:', fileId);
  },

  // 파일 상태 변경 (soft delete)
  changeFileStatus: async (fileId, status) => {
    const { updateFile, loadFiles } = get();
    
    try {
      // TODO: 백엔드에 파일 상태 변경 API 추가 후 연동
      // await fileAPI.changeStatus(fileId, status);
      
      updateFile(fileId, { status });
      
      // 현재 필터가 변경된 상태와 맞지 않으면 목록 새로고침
      const currentFilter = get().filters.status;
      if (currentFilter !== 'all' && currentFilter !== status) {
        await loadFiles();
      }
      
    } catch (error) {
      console.error('[FILE_STORE]', 'Change file status error:', error);
      throw error;
    }
  },

  // 에러 클리어
  clearError: () => {
    set({ error: null });
  },

  // 스토어 초기화
  reset: () => {
    set({
      files: [],
      pagination: {
        current_page: 1,
        total_pages: 0,
        total_files: 0,
        has_next: false,
        has_prev: false
      },
      loading: false,
      error: null,
      filters: {
        status: 'active',
        page: 1,
        limit: 20,
        sort_by: 'uploaded_at',
        sort_order: 'desc',
        search: ''
      }
    });
  },

  // 파일 존재 여부 확인
  hasFile: (fileId) => {
    const { files } = get();
    return files.some(file => file.file_id === fileId);
  },

  // ID로 파일 찾기
  getFileById: (fileId) => {
    const { files } = get();
    return files.find(file => file.file_id === fileId);
  },

  // 파일 개수 가져오기
  getFileCount: () => {
    const { pagination } = get();
    return pagination.total_files;
  }
}));

export default useFileStore;